import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { FolderStructurePreview } from "@/components/features/settings/defaults/matter-file-repository-structures/FolderStructurePreview";
import { MatterFileRepositoryStructureContextMenu } from "@/components/features/settings/defaults/matter-file-repository-structures/MatterFileRepositoryStructureContextMenu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import {
  ActionEmptyState,
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveDefaultMatterFileRepositoryStructureFolderQueryOptions } from "@/lib/data/react-query/matters/matter-default-file-repository-structure-folder";
import { useDeleteDefaultMatterFileRepositoryStructure } from "@/lib/data/react-query/matters/matter-default-file-repository-structure";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type DefaultMatterFileRepositoryStructure,
  type IncidentType,
} from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { ChevronRight, Eye, Folder, Pencil } from "lucide-react";
import pluralize from "pluralize";
import { useState } from "react";
import { AddOrEditMatterFileRepositoryStructureModal } from "./AddOrEditMatterFileRepositoryStructureModal";

export interface MatterFileRepositoryStructuresTableProps {
  isAddingFileRepoStructure: boolean;
  setIsAddingFileRepoStructure: (open: boolean) => void;
  canCreate: boolean;
}

function DangerousMatterFileRepositoryStructuresTable({
  isAddingFileRepoStructure,
  setIsAddingFileRepoStructure,
  canCreate,
}: MatterFileRepositoryStructuresTableProps) {
  const [isEditingFileRepoStructure, setIsEditingFileRepoStructure] =
    useState(false);
  const [activeFileRepoStructureId, setActiveFileRepoStructureId] = useState<
    string | null
  >(null);
  const [structurePendingDelete, setStructurePendingDelete] =
    useState<DefaultMatterFileRepositoryStructure | null>(null);
  const { hasActionPermission } = useUser();

  const {
    data: defaultMatterFileRepositoryStructuresData,
    isLoading: isDefaultMatterFileRepositoryStructuresLoading,
    isError: isDefaultMatterFileRepositoryStructuresError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const defaultMatterFileRepositoryStructures =
    defaultMatterFileRepositoryStructuresData?.data
      .defaultFileRepositoryStructures ?? [];

  const activeFileRepoStructure = defaultMatterFileRepositoryStructures.find(
    (type) => type.id === activeFileRepoStructureId,
  );

  const formattedDefaultMatterFileRepositoryStructures =
    defaultMatterFileRepositoryStructures.map((structure) => ({
      ...structure,
      incidentTypes: (structure.incidentTypeIds ?? [])
        .map((id) =>
          defaultMatterFileRepositoryStructuresData?.data.incidentTypes?.find(
            (incidentType) => incidentType.id === id,
          ),
        )
        .filter((incidentType) => incidentType !== undefined),
    })) as (DefaultMatterFileRepositoryStructure & {
      incidentTypes: IncidentType[];
    })[];

  const folderListQueries = useQueries({
    queries: formattedDefaultMatterFileRepositoryStructures.map((structure) =>
      retrieveDefaultMatterFileRepositoryStructureFolderQueryOptions(
        structure.id,
      ),
    ),
  });

  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterFileRepositoryStructure,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterFileRepositoryStructure,
    ACTION_PERMISSION_VALUES.delete,
  );
  const deleteDefaultMatterFileRepositoryStructure =
    useDeleteDefaultMatterFileRepositoryStructure();

  if (isDefaultMatterFileRepositoryStructuresError) {
    return <Text variant="subHeading">{serverErrorFallbackText}</Text>;
  }

  return (
    <div className="w-full">
      {isDefaultMatterFileRepositoryStructuresLoading ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-border/60 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="h-14 w-full animate-pulse rounded-md bg-neutral-100" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : formattedDefaultMatterFileRepositoryStructures.length === 0 ? (
        <ActionEmptyState
          title="No folder structures yet"
          description="Add a structure to define default folders for your incident types."
          actions={
            canCreate ? (
              <Button
                onClick={() => {
                  setIsAddingFileRepoStructure(true);
                }}
              >
                Add structure
              </Button>
            ) : null
          }
        />
      ) : (
        <div
          data-testid="matter-default-file-repository-structure-table"
          className="flex w-full flex-col gap-4"
        >
          <Accordion
            type="single"
            collapsible
            className="flex w-full flex-col gap-4"
          >
            {formattedDefaultMatterFileRepositoryStructures.map(
              (structure, structureIndex) => {
                const typeLabels =
                  structure.incidentTypes.map(getIncidentTypeLabel);
                const folderQuery = folderListQueries[structureIndex];
                const folderCount = folderQuery.data?.data.length ?? 0;
                const folders = folderQuery.data?.data ?? [];
                const isFoldersLoading = folderQuery.isLoading;

                return (
                  <AccordionItem
                    key={structure.id}
                    value={structure.id}
                    className="border-0"
                  >
                    <Card className="border-border/60 overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <MatterFileRepositoryStructureContextMenu
                          structure={structure}
                          canEdit={canUpdate}
                          canDelete={canDelete}
                          onEdit={() => {
                            setActiveFileRepoStructureId(structure.id);
                            setIsEditingFileRepoStructure(true);
                          }}
                          onDelete={() => {
                            setStructurePendingDelete(structure);
                          }}
                          trigger={
                            <div className="group hover:bg-light-primary/50 flex w-full cursor-pointer items-center gap-3 px-4 py-5 transition-colors sm:px-6">
                              <AccordionTrigger
                                className="min-w-0 flex-1 cursor-pointer items-center py-0 hover:bg-transparent hover:no-underline [&[data-state=open]_.folder-structure-chevron]:rotate-90"
                                icon={<></>}
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                                  <ChevronRight
                                    className="folder-structure-chevron text-muted-foreground h-4 w-4 shrink-0 self-center transition-transform duration-200"
                                    aria-hidden
                                  />
                                  <div
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-100 bg-amber-50"
                                    aria-hidden
                                  >
                                    <Folder className="h-4 w-4 text-amber-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <Text variant="body">
                                      {structure.label}
                                    </Text>
                                    {structure.description ? (
                                      <Text
                                        variant="caption"
                                        className="mt-0.5"
                                      >
                                        {structure.description}
                                      </Text>
                                    ) : null}
                                    {typeLabels.length > 0 ? (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {typeLabels.map((label) => (
                                          <Badge
                                            key={label}
                                            variant="outline"
                                            className="border-border/70 bg-muted/55 text-foreground/85 font-normal shadow-none"
                                          >
                                            {label}
                                          </Badge>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                  <Text
                                    as="span"
                                    variant="subHeading"
                                    className="shrink-0 self-center whitespace-nowrap"
                                  >
                                    {folderCount}{" "}
                                    {pluralize("folder", folderCount)}
                                  </Text>
                                </div>
                              </AccordionTrigger>

                              {canUpdate ? (
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground focus-visible:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-md opacity-100 transition-[opacity,color,background-color] sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setActiveFileRepoStructureId(structure.id);
                                    setIsEditingFileRepoStructure(true);
                                  }}
                                >
                                  <span className="sr-only">
                                    Edit {structure.label}
                                  </span>
                                  <Pencil className="h-4 w-4" aria-hidden />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground focus-visible:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-md opacity-100 transition-[opacity,color,background-color] sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setActiveFileRepoStructureId(structure.id);
                                    setIsEditingFileRepoStructure(true);
                                  }}
                                >
                                  <span className="sr-only">
                                    View {structure.label}
                                  </span>
                                  <Eye className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </div>
                          }
                        />
                        <AccordionContent className="px-4 pb-4 sm:px-6">
                          <div className="border-border/60 border-t pt-4">
                            <FolderStructurePreview
                              folders={folders}
                              isLoading={isFoldersLoading}
                              onEdit={
                                canUpdate
                                  ? () => {
                                      setActiveFileRepoStructureId(
                                        structure.id,
                                      );
                                      setIsEditingFileRepoStructure(true);
                                    }
                                  : undefined
                              }
                            />
                          </div>
                        </AccordionContent>
                      </CardContent>
                    </Card>
                  </AccordionItem>
                );
              },
            )}
          </Accordion>
        </div>
      )}

      <AddOrEditMatterFileRepositoryStructureModal
        editing={false}
        open={isAddingFileRepoStructure}
        setOpen={setIsAddingFileRepoStructure}
      />
      {activeFileRepoStructure ? (
        <AddOrEditMatterFileRepositoryStructureModal
          activeMatterFileRepositoryStructure={activeFileRepoStructure}
          isReadOnly={!canUpdate}
          editing={true}
          open={isEditingFileRepoStructure}
          setOpen={setIsEditingFileRepoStructure}
          setActiveMatterFileRepositoryStructureId={
            setActiveFileRepoStructureId
          }
        />
      ) : null}
      <ActionConfirmationModal
        title="Are you sure you want to delete this folder structure?"
        open={Boolean(structurePendingDelete)}
        setOpen={(open) => {
          if (!open) setStructurePendingDelete(null);
        }}
        isActionPending={deleteDefaultMatterFileRepositoryStructure.isPending}
        afterActionFn={async () => {
          if (!structurePendingDelete) return;
          await deleteDefaultMatterFileRepositoryStructure.mutateAsync({
            id: structurePendingDelete.id,
          });
          setStructurePendingDelete(null);
        }}
      />
    </div>
  );
}

export function MatterFileRepositoryStructuresTable(
  props: MatterFileRepositoryStructuresTableProps,
) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-4">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <DangerousMatterFileRepositoryStructuresTable {...props} />
    </ErrorBoundary>
  );
}
